import {
  Settings,
  Trophy,
  Gauge,
  User,
  Users,
  Handshake,
  ShoppingCart,
  DollarSign,
  Monitor,
  LogOut,
  Tv,
  Swords,
  Medal,
  BadgeDollarSign,
} from 'lucide-react';
import { tennisBall } from '@lucide/lab';
import { Link } from 'react-router';
import { useAtomValue } from 'jotai';
import type { Club } from '@repo/shared-types';
import { logout } from '@repo/api-client/lib/auth';
import { userAtom } from '@repo/api-client/stores/auth';
import { DropdownMenuItem } from '@repo/ui/components/dropdown-menu';
import { NavMain, type NavItem } from '@repo/ui/components/nav-main';
import { NavUser } from '@repo/ui/components/nav-user';
import {
  Sidebar as SidebarRoot,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@repo/ui/components/sidebar';
import {
  urlCashierMode,
  urlConfig,
  urlControlPanel,
  urlCourtList,
  urlPlayerList,
  urlProductList,
  urlTournamentList,
  urlOpenMatches,
  urlSales,
  urlProfile,
  urlRanking,
  urlScreenList,
  urlSponsors,
  urlTournamentMode,
} from '@/urls';
import { isClubOwnerOrManager } from '@repo/ui/lib/club-role';
import { useActiveTournaments } from '@/hooks/use-active-tournaments';
import { ClubSwitcher } from './club-switcher';

type Feature = keyof Club['features'];
type ClubNavItem = NavItem & { feature?: Feature };

function byFeature(club: Club) {
  return (item: ClubNavItem) => !item.feature || !!club.features?.[item.feature];
}

export function Sidebar({ club }: { club: Club }) {
  const user = useAtomValue(userAtom);
  const [activeTournament] = useActiveTournaments(club.id);
  const tournamentMode = club.features?.tournaments ? activeTournament : undefined;

  const operation: ClubNavItem[] = [
    { title: 'Panel de control', url: urlControlPanel(club.id), icon: Gauge, end: true },
    { title: 'Ventas', url: urlSales(club.id), icon: DollarSign, feature: 'pointsOfSale' },
    { title: 'Torneos', url: urlTournamentList(club.id), icon: Trophy, feature: 'tournaments' },
    { title: 'Ranking', url: urlRanking(club.id), icon: Medal, feature: 'tournaments' },
    {
      title: 'Partidos abiertos',
      url: urlOpenMatches(club.id),
      icon: Handshake,
      feature: 'openMatches',
    },
  ];
  const management: ClubNavItem[] = [
    { title: 'Jugadores', url: urlPlayerList(club.id), icon: Users, feature: 'players' },
    {
      title: 'Productos',
      url: urlProductList(club.id),
      icon: ShoppingCart,
      feature: 'pointsOfSale',
      badge: club.features?.stock ? club.lowStockCount : undefined,
    },
    { title: 'Canchas', url: urlCourtList(club.id), icon: tennisBall, feature: 'pointsOfSale' },
    { title: 'Pantallas', url: urlScreenList(club.id), icon: Tv, feature: 'screens' },
    ...(isClubOwnerOrManager(club)
      ? [
          {
            title: 'Sponsors',
            url: urlSponsors(club.id),
            icon: BadgeDollarSign,
            feature: 'tournaments' as const,
          },
        ]
      : []),
    { title: 'Configuración', url: urlConfig(club.id), icon: Settings },
  ];

  return (
    <SidebarRoot variant="inset" collapsible="icon">
      <SidebarHeader>
        <ClubSwitcher club={club} />
        {(club.features?.pointsOfSale || tournamentMode) && (
          <SidebarMenu>
            {club.features?.pointsOfSale && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  tooltip="Modo caja"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                >
                  <Link to={urlCashierMode(club.id)} viewTransition>
                    <Monitor />
                    <span>Modo caja</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
            {tournamentMode && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  tooltip="Modo torneo"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                >
                  <Link to={urlTournamentMode(club.id, tournamentMode.id)} viewTransition>
                    <Swords />
                    <span>Modo torneo</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
          </SidebarMenu>
        )}
      </SidebarHeader>
      <SidebarContent>
        <NavMain label="Operación" items={operation.filter(byFeature(club))} />
        <NavMain label="Gestión" items={management.filter(byFeature(club))} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser
          name={[user?.firstName, user?.lastName].filter(Boolean).join(' ')}
          email={user?.email ?? ''}
          picture={user?.picture}
        >
          <DropdownMenuItem asChild>
            <Link to={urlProfile(club.id)} viewTransition>
              <User />
              Perfil
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={logout}>
            <LogOut />
            Cerrar sesión
          </DropdownMenuItem>
        </NavUser>
      </SidebarFooter>
    </SidebarRoot>
  );
}
